import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, RadarChart, Radar, PolarGrid,
  PolarAngleAxis, PolarRadiusAxis, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  Users, TrendingUp, TrendingDown, AlertTriangle, Award, Shield, BarChart2,
  Download, AlertCircle, CheckCircle, XCircle, Target, Activity
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#16A34A', '#C5A572', '#1B2A4A', '#DC2626'];
const CHART_COLORS = {
  pass: '#16A34A',
  merit: '#C5A572',
  distinction: '#1B2A4A',
  fail: '#DC2626',
  primary: '#1B2A4A',
  secondary: '#C5A572',
  danger: '#DC2626',
  success: '#16A34A'
};

function KPICard({ title, value, subtitle, icon: Icon, trend, trendValue, color = 'text-[#1B2A4A]' }) {
  return (
    <Card className="imcac-card">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wide">{title}</p>
            <p className={`text-2xl font-bold ${color} mt-1`}>{value}</p>
            {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
          </div>
          <div className={`p-2 rounded-sm ${color === 'text-red-600' ? 'bg-red-100' : 'bg-slate-100'}`}>
            <Icon className={`h-5 w-5 ${color}`} />
          </div>
        </div>
        {trend && (
          <div className={`flex items-center mt-2 text-xs ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
            {trend === 'up' ? <TrendingUp className="h-3 w-3 mr-1" /> : <TrendingDown className="h-3 w-3 mr-1" />}
            {trendValue}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AlertBanner({ alerts }) {
  if (!alerts || alerts.length === 0) return null;
  
  const criticalAlerts = alerts.filter(a => a.severity === 'high' || a.severity === 'critical');
  const warningAlerts = alerts.filter(a => a.severity === 'medium');
  
  if (criticalAlerts.length === 0 && warningAlerts.length === 0) return null;
  
  return (
    <div className="space-y-3 mb-6" data-testid="alert-banner">
      {criticalAlerts.length > 0 && (
        <div className="bg-red-100 border-l-4 border-red-500 rounded-sm p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-red-800">Critical Alerts ({criticalAlerts.length})</h3>
              {criticalAlerts.map((alert, idx) => (
                <div key={idx} className="flex items-center gap-2 mt-2">
                  <span className="inline-block w-2 h-2 rounded-full bg-red-500"></span>
                  <p className="text-sm text-red-700">{alert.message}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {warningAlerts.length > 0 && (
        <div className="bg-amber-50 border-l-4 border-amber-400 rounded-sm p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-amber-800">Warnings ({warningAlerts.length})</h3>
              {warningAlerts.map((alert, idx) => (
                <div key={idx} className="flex items-center gap-2 mt-2">
                  <span className="inline-block w-2 h-2 rounded-full bg-amber-500"></span>
                  <p className="text-sm text-amber-700">{alert.message}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AlertsPanel({ alerts }) {
  const criticalAlerts = alerts?.filter(a => a.severity === 'high' || a.severity === 'critical') || [];
  const warningAlerts = alerts?.filter(a => a.severity === 'medium') || [];
  const infoAlerts = alerts?.filter(a => a.severity === 'low' || a.severity === 'info') || [];

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'high':
      case 'critical':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      case 'medium':
        return <AlertCircle className="h-4 w-4 text-amber-500" />;
      default:
        return <Activity className="h-4 w-4 text-blue-500" />;
    }
  };

  const getSeverityStyle = (severity) => {
    switch (severity) {
      case 'high':
      case 'critical':
        return 'bg-red-50 border-red-200';
      case 'medium':
        return 'bg-amber-50 border-amber-200';
      default:
        return 'bg-blue-50 border-blue-200';
    }
  };

  return (
    <div className="space-y-4" data-testid="alerts-panel">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-red-600">{criticalAlerts.length}</p>
            <p className="text-sm text-red-700">Critical Alerts</p>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-amber-600">{warningAlerts.length}</p>
            <p className="text-sm text-amber-700">Warnings</p>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-blue-600">{infoAlerts.length}</p>
            <p className="text-sm text-blue-700">Informational</p>
          </CardContent>
        </Card>
      </div>

      <Card className="imcac-card">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold text-[#1B2A4A] mb-4">All Governance Alerts</h3>
          {alerts?.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <CheckCircle className="h-12 w-12 mx-auto mb-3 text-green-500" />
              <p>No alerts at this time. All systems operating normally.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {alerts?.map((alert, idx) => (
                <div key={alert.id || idx} className={`border rounded-sm p-4 ${getSeverityStyle(alert.severity)}`}>
                  <div className="flex items-start gap-3">
                    {getSeverityIcon(alert.severity)}
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-slate-800">{alert.category || alert.type?.replace(/_/g, ' ').toUpperCase()}</span>
                        <Badge className={`text-xs ${
                          alert.severity === 'high' || alert.severity === 'critical' ? 'bg-red-100 text-red-700' :
                          alert.severity === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {alert.severity}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-600 mt-1">{alert.message}</p>
                      {alert.current_value !== undefined && (
                        <div className="flex gap-4 text-xs text-slate-500 mt-2">
                          <span>Current: <strong>{alert.current_value}</strong></span>
                          {alert.previous_value !== undefined && <span>Previous: {alert.previous_value}</span>}
                          {alert.threshold && <span>Threshold: {alert.threshold}</span>}
                        </div>
                      )}
                      {alert.program && (
                        <p className="text-xs text-slate-500 mt-1">Program: {alert.program}</p>
                      )}
                      {alert.recommendation && (
                        <div className="mt-3 p-2 bg-slate-50 rounded border-l-2 border-slate-300">
                          <p className="text-xs text-slate-600">
                            <strong>Recommendation:</strong> {alert.recommendation}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function AnalyticsDashboard() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('90');
  const [program, setProgram] = useState('all');
  
  const [overview, setOverview] = useState(null);
  const [passTrend, setPassTrend] = useState([]);
  const [programPerf, setProgramPerf] = useState([]);
  const [safetyData, setSafetyData] = useState(null);
  const [componentPerf, setComponentPerf] = useState(null);
  const [classification, setClassification] = useState(null);
  const [examinerPerf, setExaminerPerf] = useState(null);
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    fetchAllData();
  }, [period, program]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAllData = async () => {
    setLoading(true);
    const headers = { Authorization: `Bearer ${token}` };
    const programParam = program !== 'all' ? `&program=${program}` : '';
    
    try {
      const [overviewRes, trendRes, progRes, safetyRes, compRes, classRes, examRes, alertRes] = await Promise.all([
        axios.get(`${API}/admin/analytics/overview?days=${period}${programParam}`, { headers }),
        axios.get(`${API}/admin/analytics/pass-rate-trend?months=12${programParam}`, { headers }),
        axios.get(`${API}/admin/analytics/program-performance?days=${period}`, { headers }),
        axios.get(`${API}/admin/analytics/safety-failures?days=${period}`, { headers }),
        axios.get(`${API}/admin/analytics/component-performance?days=${period}${programParam}`, { headers }),
        axios.get(`${API}/admin/analytics/classification-distribution?days=${period}`, { headers }),
        axios.get(`${API}/admin/analytics/examiner-performance?days=${period}`, { headers }),
        axios.get(`${API}/admin/analytics/alerts`, { headers })
      ]);
      
      setOverview(overviewRes.data);
      setPassTrend(trendRes.data);
      setProgramPerf(progRes.data);
      setSafetyData(safetyRes.data);
      setComponentPerf(compRes.data);
      setClassification(classRes.data);
      setExaminerPerf(examRes.data);
      setAlerts(alertRes.data.alerts || []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const exportReport = async (type) => {
    toast.info(`Generating ${type} report...`);
    // In production, this would generate a PDF/Excel
    setTimeout(() => toast.success('Report downloaded'), 1000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <p className="text-slate-500">Loading analytics...</p>
      </div>
    );
  }

  return (
    <div className="bg-[#F8FAFC] min-h-screen" data-testid="analytics-dashboard">
      {/* Header */}
      <section className="bg-[#1B2A4A] py-8">
        <div className="imcac-container">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-[#C5A572] mb-1">Governance Dashboard</p>
              <h1 className="text-2xl md:text-3xl font-bold text-white tracking-tight font-heading">
                Examination Analytics
              </h1>
            </div>
            <div className="flex flex-wrap gap-3">
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="w-36 bg-white/10 text-white border-white/20">
                  <SelectValue placeholder="Period" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">Last 30 Days</SelectItem>
                  <SelectItem value="90">Last 90 Days</SelectItem>
                  <SelectItem value="365">This Year</SelectItem>
                  <SelectItem value="9999">All Time</SelectItem>
                </SelectContent>
              </Select>
              <Select value={program} onValueChange={setProgram}>
                <SelectTrigger className="w-36 bg-white/10 text-white border-white/20">
                  <SelectValue placeholder="Program" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Programs</SelectItem>
                  <SelectItem value="CBEAM">CBEAM</SelectItem>
                  <SelectItem value="CBELM">CBELM</SelectItem>
                  <SelectItem value="CBERM">CBERM</SelectItem>
                  <SelectItem value="CBEAD">CBEAD</SelectItem>
                  <SelectItem value="CBENA">CBENA</SelectItem>
                  <SelectItem value="CBEAA">CBEAA</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={() => exportReport('pdf')} variant="outline" className="bg-white/10 text-white border-white/20 hover:bg-white/20">
                <Download className="h-4 w-4 mr-2" /> Export Report
              </Button>
            </div>
          </div>
        </div>
      </section>

      <div className="imcac-container py-6">
        {/* Alert Banner */}
        <AlertBanner alerts={alerts} />

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-6">
          <KPICard title="Total Candidates" value={overview?.total_candidates || 0} icon={Users} />
          <KPICard title="Pass Rate" value={`${overview?.pass_rate || 0}%`} icon={CheckCircle} color="text-green-600" />
          <KPICard title="Fail Rate" value={`${overview?.fail_rate || 0}%`} icon={XCircle} color="text-red-600" />
          <KPICard title="Safety Fails" value={`${overview?.safety_fail_rate || 0}%`} icon={AlertTriangle} color={overview?.safety_fail_rate > 15 ? 'text-red-600' : 'text-amber-600'} />
          <KPICard title="Merit" value={`${overview?.merit_rate || 0}%`} icon={Award} color="text-[#C5A572]" />
          <KPICard title="Distinction" value={`${overview?.distinction_rate || 0}%`} icon={Award} color="text-[#1B2A4A]" />
          <KPICard title="Avg Score" value={`${overview?.average_score || 0}%`} icon={Target} />
          <KPICard title="Certificates" value={overview?.certificates_issued || 0} icon={Shield} color="text-green-600" />
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="bg-white border border-slate-200 p-1 rounded-sm flex-wrap">
            <TabsTrigger value="overview" className="rounded-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white">
              <BarChart2 className="h-4 w-4 mr-2" /> Overview
            </TabsTrigger>
            <TabsTrigger value="safety" className="rounded-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white">
              <AlertTriangle className="h-4 w-4 mr-2" /> Safety Monitoring
            </TabsTrigger>
            <TabsTrigger value="components" className="rounded-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white">
              <Activity className="h-4 w-4 mr-2" /> Components
            </TabsTrigger>
            <TabsTrigger value="examiners" className="rounded-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white">
              <Users className="h-4 w-4 mr-2" /> Examiners
            </TabsTrigger>
            <TabsTrigger value="alerts" className="rounded-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white relative">
              <AlertCircle className="h-4 w-4 mr-2" /> Alerts
              {alerts?.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                  {alerts.length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Pass/Fail Distribution */}
              <Card className="imcac-card">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-[#1B2A4A] mb-4">Result Distribution</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={classification?.distribution || []}
                        dataKey="count"
                        nameKey="classification"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label={({ name, percentage }) => `${name}: ${percentage}%`}
                      >
                        {classification?.distribution?.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Pass Rate Trend */}
              <Card className="imcac-card">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-[#1B2A4A] mb-4">Pass Rate Trend (12 Months)</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={passTrend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                      <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                      <Tooltip />
                      <Line type="monotone" dataKey="pass_rate" stroke={CHART_COLORS.primary} strokeWidth={2} dot={{ fill: CHART_COLORS.primary }} />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Program Performance */}
              <Card className="imcac-card lg:col-span-2">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-[#1B2A4A] mb-4">Program-wise Pass Rate</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={programPerf} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis type="number" domain={[0, 100]} />
                      <YAxis dataKey="program" type="category" width={80} tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Bar dataKey="pass_rate" fill={CHART_COLORS.success} name="Pass Rate %" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Classification by Program */}
              <Card className="imcac-card lg:col-span-2">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-[#1B2A4A] mb-4">Classification Distribution by Program</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={classification?.by_program || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="program" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="pass" stackId="a" fill={CHART_COLORS.success} name="Certified" />
                      <Bar dataKey="merit" stackId="a" fill={CHART_COLORS.secondary} name="Merit" />
                      <Bar dataKey="distinction" stackId="a" fill={CHART_COLORS.primary} name="Distinction" />
                      <Bar dataKey="fail" stackId="a" fill={CHART_COLORS.danger} name="Fail" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Safety Monitoring Tab */}
          <TabsContent value="safety" className="space-y-6">
            {safetyData?.alert_threshold_exceeded && (
              <div className="bg-red-100 border-2 border-red-400 rounded-sm p-4 flex items-center gap-3">
                <AlertCircle className="h-6 w-6 text-red-600" />
                <div>
                  <p className="font-semibold text-red-800">Safety Alert: Threshold Exceeded</p>
                  <p className="text-sm text-red-700">Safety failure rate ({safetyData.safety_fail_percentage}%) exceeds 15% threshold</p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="imcac-card">
                <CardContent className="p-6 text-center">
                  <p className="text-xs text-slate-500 uppercase">Total Safety Fails</p>
                  <p className="text-4xl font-bold text-red-600 mt-2">{safetyData?.total_safety_fails || 0}</p>
                  <p className="text-sm text-slate-500 mt-1">out of {safetyData?.total_candidates || 0} candidates</p>
                </CardContent>
              </Card>
              <Card className="imcac-card">
                <CardContent className="p-6 text-center">
                  <p className="text-xs text-slate-500 uppercase">Safety Fail Rate</p>
                  <p className={`text-4xl font-bold mt-2 ${safetyData?.safety_fail_percentage > 15 ? 'text-red-600' : 'text-amber-600'}`}>
                    {safetyData?.safety_fail_percentage || 0}%
                  </p>
                  <Badge className={`mt-2 ${safetyData?.safety_fail_percentage > 15 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                    {safetyData?.safety_fail_percentage > 15 ? 'Above Threshold' : 'Within Threshold'}
                  </Badge>
                </CardContent>
              </Card>
              <Card className="imcac-card">
                <CardContent className="p-6 text-center">
                  <p className="text-xs text-slate-500 uppercase">Highest Risk Area</p>
                  <p className="text-lg font-bold text-[#1B2A4A] mt-2">{safetyData?.highest_risk_area || 'None'}</p>
                  <p className="text-xs text-red-600 mt-1">Requires attention</p>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Safety Categories */}
              <Card className="imcac-card">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-[#1B2A4A] mb-4">Safety Failures by Category</h3>
                  <div className="space-y-4">
                    {safetyData?.categories && Object.entries(safetyData.categories).map(([key, val]) => (
                      <div key={key} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-3 h-3 rounded-full ${val.count > 0 ? 'bg-red-500' : 'bg-slate-300'}`} />
                          <span className="text-sm text-slate-700">{val.label}</span>
                        </div>
                        <Badge className={val.count > 0 ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-500'}>
                          {val.count}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Safety Trend */}
              <Card className="imcac-card">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-[#1B2A4A] mb-4">Safety Failure Trend</h3>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={safetyData?.monthly_trend || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip />
                      <Line type="monotone" dataKey="count" stroke={CHART_COLORS.danger} strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Components Tab */}
          <TabsContent value="components" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Radar Chart */}
              <Card className="imcac-card">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-[#1B2A4A] mb-4">Component Performance Radar</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <RadarChart data={componentPerf?.components || []}>
                      <PolarGrid />
                      <PolarAngleAxis dataKey="component" tick={{ fontSize: 11 }} />
                      <PolarRadiusAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                      <Radar name="Average Score" dataKey="average_score" stroke={CHART_COLORS.primary} fill={CHART_COLORS.primary} fillOpacity={0.5} />
                      <Tooltip />
                    </RadarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Bar Chart */}
              <Card className="imcac-card">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-[#1B2A4A] mb-4">Component Average Scores</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={componentPerf?.components || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="component" tick={{ fontSize: 11 }} />
                      <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                      <Tooltip />
                      <Bar dataKey="average_score" fill={CHART_COLORS.primary} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>

            {/* Insights */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="imcac-card border-l-4 border-l-green-500">
                <CardContent className="p-6">
                  <p className="text-xs text-slate-500 uppercase">Highest Performing Component</p>
                  <p className="text-xl font-bold text-green-600 mt-2">{componentPerf?.highest_component || 'N/A'}</p>
                  <p className="text-sm text-slate-500 mt-1">Candidates perform best in this area</p>
                </CardContent>
              </Card>
              <Card className="imcac-card border-l-4 border-l-red-500">
                <CardContent className="p-6">
                  <p className="text-xs text-slate-500 uppercase">Lowest Performing Component</p>
                  <p className="text-xl font-bold text-red-600 mt-2">{componentPerf?.lowest_component || 'N/A'}</p>
                  <p className="text-sm text-slate-500 mt-1">May require curriculum review</p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Examiners Tab */}
          <TabsContent value="examiners" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <Card className="imcac-card">
                <CardContent className="p-4 text-center">
                  <p className="text-xs text-slate-500">Total Examiners</p>
                  <p className="text-2xl font-bold text-[#1B2A4A]">{examinerPerf?.total_examiners || 0}</p>
                </CardContent>
              </Card>
              <Card className="imcac-card">
                <CardContent className="p-4 text-center">
                  <p className="text-xs text-slate-500">Overall Average Score</p>
                  <p className="text-2xl font-bold text-[#1B2A4A]">{examinerPerf?.overall_average || 0}%</p>
                </CardContent>
              </Card>
              <Card className="imcac-card border-amber-400 border-2">
                <CardContent className="p-4 text-center">
                  <p className="text-xs text-slate-500">Flagged Examiners</p>
                  <p className="text-2xl font-bold text-amber-600">{examinerPerf?.outliers?.length || 0}</p>
                  <p className="text-xs text-amber-600">{'>'}15% deviation from mean</p>
                </CardContent>
              </Card>
            </div>

            {/* Outliers Alert */}
            {examinerPerf?.outliers?.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 mb-6">
                <h4 className="font-semibold text-amber-800 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" /> Examiners Flagged for Moderation Review
                </h4>
                <div className="flex flex-wrap gap-2 mt-2">
                  {examinerPerf.outliers.map((e, idx) => (
                    <Badge key={idx} className="bg-amber-100 text-amber-800">
                      {e.examiner_name} ({e.deviation_percentage}% deviation)
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Examiner Table */}
            <Card className="imcac-card">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-[#1B2A4A] mb-4">Examiner Consistency Analysis</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50">
                        <th className="text-left p-3 font-semibold">Examiner</th>
                        <th className="text-center p-3 font-semibold">Candidates</th>
                        <th className="text-center p-3 font-semibold">Avg Score</th>
                        <th className="text-center p-3 font-semibold">Fail Rate</th>
                        <th className="text-center p-3 font-semibold">Safety Fail %</th>
                        <th className="text-center p-3 font-semibold">Deviation</th>
                        <th className="text-center p-3 font-semibold">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {examinerPerf?.examiners?.map((examiner, idx) => (
                        <tr key={idx} className={`border-b border-slate-100 ${examiner.flagged ? 'bg-amber-50' : ''}`}>
                          <td className="p-3 font-medium">{examiner.examiner_name}</td>
                          <td className="p-3 text-center">{examiner.total_candidates}</td>
                          <td className="p-3 text-center">{examiner.average_score}%</td>
                          <td className="p-3 text-center">{examiner.fail_rate}%</td>
                          <td className="p-3 text-center">{examiner.safety_fail_rate}%</td>
                          <td className={`p-3 text-center font-medium ${Math.abs(examiner.deviation_from_mean) > 10 ? 'text-amber-600' : 'text-slate-600'}`}>
                            {examiner.deviation_from_mean > 0 ? '+' : ''}{examiner.deviation_from_mean}
                          </td>
                          <td className="p-3 text-center">
                            {examiner.flagged ? (
                              <Badge className="bg-amber-100 text-amber-700">Flagged</Badge>
                            ) : (
                              <Badge className="bg-green-100 text-green-700">Normal</Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Alerts Tab */}
          <TabsContent value="alerts" className="space-y-6">
            <AlertsPanel alerts={alerts} />
          </TabsContent>
        </Tabs>

        {/* Footer Disclaimer */}
        <p className="text-xs text-slate-400 text-center mt-8 px-4">
          Analytics data is for internal governance and quality assurance purposes only. 
          IMCAC Internal Governance Report – Confidential
        </p>
      </div>
    </div>
  );
}
